import React, { useEffect, useState, useMemo } from 'react';
import { Table, Button, Modal, Form, Select, TimePicker, message } from 'antd';
import type { TableColumnsType } from 'antd';
import dayjs from 'dayjs';
import { useQuery, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { GET_WAREHOUSE_PLANNING_ENTRY_CONFIGS, GET_AVAILABLE_ENTRY_NODES, WarehousePlanningEntryConfigType, WarehousePlanningEntryConfigFiltersInput, GetWarehousePlanningEntryConfigsResponse, GetAvailableEntryNodesResponse } from './graphql/queries/getWarehousePlanningEntryConfigs';
import { UPDATE_WAREHOUSE_PLANNING_ENTRY_CONFIG, UpdateWarehousePlanningEntryConfigInput, UpdateWarehousePlanningEntryConfigResponse } from './graphql/mutations/updateWarehousePlanningEntryConfig';
import { CREATE_WAREHOUSE_PLANNING_ENTRY_CONFIGS, CreateWarehousePlanningEntryConfigInput, CreateWarehousePlanningEntryConfigResponse } from './graphql/mutations/createWarehousePlanningEntryConfig';
import { useWarehouseOptions } from '../../../hooks/useWarehouseOptions';
import { SiteType } from '../../../graphql/types/SiteType';
import { formatISODateTime } from '../../../utils/dateTimeUtils';
import WarehousePlanningEntryConfigFilter from './components/WarehousePlanningEntryConfigFilter';
import TablePaginationBar from '../../../components/TablePaginationBar';

const WarehousePlanningEntryConfigPage: React.FC = () => {
    const { t } = useTranslation();
    // 筛选控件返回的过滤条件
    const [filters, setFilters] = useState<WarehousePlanningEntryConfigFiltersInput>({});
    const [modalVisible, setModalVisible] = useState(false);
    // 表单 （是创建还是修改状态弹窗）
    const [modalMode, setModalMode] = useState<'create' | 'edit'>('edit');
    // 从库里取ID传给修改弹框
    const [editingConfig, setEditingConfig] = useState<WarehousePlanningEntryConfigType | null>(null);
    // 表单（创建/修改配置弹框）
    const [form] = Form.useForm();
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
    });
    // 提示信息弹窗
    const [infoModalVisible, setInfoModalVisible] = useState(false);
    const [infoModalContent, setInfoModalContent] = useState<string>('');
    // 确认保存配置弹窗
    const [confirmModalVisible, setConfirmModalVisible] = useState(false);

    // 格式化批量创建结果消息
    const formatBatchCreateResult = (result: { successCount: number; skippedCount: number; skippedWarehouses: string[] }): string => {
        const successCount = result.successCount || 0;
        const skippedCount = result.skippedCount || 0;
        const skippedWarehouses = result.skippedWarehouses || [];

        const parts: string[] = [];

        // 辅助函数：获取翻译，如果翻译不存在则返回降级文本
        const getTranslation = (key: string, params: Record<string, any>, fallback: string): string => {
            const translated = t(key, params);
            // 如果翻译结果等于 key 本身，说明翻译没找到，使用降级文本
            return translated === key ? fallback : translated;
        };

        // 根据情况组合消息
        if (successCount > 0 && skippedCount > 0) {
            // 部分成功
            parts.push(getTranslation(
                'business.info.warehousePlanningEntryConfigBatchCreateResult.partialSuccess',
                { successCount, skippedCount },
                `部分配置创建成功（成功 ${successCount} 个，跳过 ${skippedCount} 个）。`
            ));
        } else if (successCount === 0 && skippedCount > 0) {
            // 全部跳过
            parts.push(getTranslation(
                'business.info.warehousePlanningEntryConfigBatchCreateResult.allSkipped',
                { skippedCount },
                `所有配置都已存在（跳过 ${skippedCount} 个）。`
            ));
        } else if (successCount > 0) {
            // 全部成功
            parts.push(getTranslation(
                'business.info.warehousePlanningEntryConfigBatchCreateResult.success',
                { count: successCount },
                `成功创建 ${successCount} 个配置`
            ));
        }

        // 跳过部分
        if (skippedWarehouses.length > 0) {
            parts.push(getTranslation(
                'business.info.warehousePlanningEntryConfigBatchCreateResult.skippedWarehouses',
                { warehouses: skippedWarehouses.join('、') },
                `以下仓库: ${skippedWarehouses.join('、')}已存在配置，请使用修改功能。`
            ));
        }

        return parts.join('\n');
    };

    // 当过滤条件变化时，重置分页
    React.useEffect(() => {
        setPagination(prev => ({ ...prev, current: 1 }));
    }, [filters]);

    // 获取仓库列表（只查询 siteType 为 2 的 warehouse）
    const { warehouseOptions: warehouseOptionsFromHook } = useWarehouseOptions(SiteType.WAREHOUSE);
    
    // 获取准入节点选项
    const { data: entryNodesData } = useQuery<GetAvailableEntryNodesResponse>(GET_AVAILABLE_ENTRY_NODES);
    
    // 获取配置列表
    const { data: configsData, loading, refetch } = useQuery<GetWarehousePlanningEntryConfigsResponse>(
        GET_WAREHOUSE_PLANNING_ENTRY_CONFIGS,
        {
            variables: { filters },
            fetchPolicy: 'no-cache'
        }
    );

    const [updateConfig, { loading: updateLoading }] = useMutation<UpdateWarehousePlanningEntryConfigResponse, { id: string; input: UpdateWarehousePlanningEntryConfigInput }>(
        UPDATE_WAREHOUSE_PLANNING_ENTRY_CONFIG
    );
    const [createConfigs, { loading: createLoading }] = useMutation<CreateWarehousePlanningEntryConfigResponse, { input: CreateWarehousePlanningEntryConfigInput }>(
        CREATE_WAREHOUSE_PLANNING_ENTRY_CONFIGS
    );

    // 表格数据
    const configs = configsData?.configs || [];

    useEffect(() => {
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, [configs.length]);

    const pagedConfigs = useMemo(() => {
        const start = (pagination.current - 1) * pagination.pageSize;
        const end = start + pagination.pageSize;
        return configs.slice(start, end);
    }, [configs, pagination.current, pagination.pageSize]);

    // 已存在配置的仓库ID集合（统一转换为数字）
    const existingWarehouseIds = useMemo(() => {
        return new Set(configs.map(config => Number(config.warehouseId)));
    }, [configs]);

    // 仓库选项（用于编辑模式，显示所有仓库）
    const warehouseOptions = useMemo(() => {
        return warehouseOptionsFromHook.map(warehouse => ({
            label: warehouse.name,
            value: Number(warehouse.id)
        }));
    }, [warehouseOptionsFromHook]);

    // 可用的仓库选项（用于创建模式，过滤掉已有配置的仓库）
    const availableWarehouseOptions = useMemo(() => {
        return warehouseOptionsFromHook
            .filter(warehouse => !existingWarehouseIds.has(Number(warehouse.id)))
            .map(warehouse => ({
                label: warehouse.name,
                value: Number(warehouse.id)
            }));
    }, [warehouseOptionsFromHook, existingWarehouseIds]);

    // 准入节点选项
    const entryNodeOptions = useMemo(() => {
        if (!entryNodesData?.entryNodes) return [];
        return entryNodesData.entryNodes.map(node => ({
            label: node.description,
            value: node.value
        }));
    }, [entryNodesData]);

    // 是否生效选项
    const isActiveOptions = [
        { label: t('config.entryConfig.isActive.yes'), value: true },
        { label: t('config.entryConfig.isActive.no'), value: false }
    ];

    const handleCreate = () => {
        setModalMode('create');
        setEditingConfig(null);
        form.resetFields();
        // 未配置拆分模式时默认 stop 拆分
        form.setFieldsValue({ splitOrderConfig: [3] });
        setModalVisible(true);
    };
    const handleEdit = (config: WarehousePlanningEntryConfigType) => {
        setModalMode('edit');
        setEditingConfig(config);
        form.setFieldsValue({
            warehouse: config.warehouseName,
            entryNode: config.entryNode,
            isActive: config.isActive,
            splitOrderConfig: config.splitOrderConfig && config.splitOrderConfig.length > 0 ? config.splitOrderConfig : [3],
            templateEditTimeRange: config.templateEditStartTime && config.templateEditEndTime
                ? [dayjs(config.templateEditStartTime, 'HH:mm'), dayjs(config.templateEditEndTime, 'HH:mm')]
                : undefined
        });
        setModalVisible(true);
    };

    // 提取清理逻辑为独立函数
    const resetFormAndCloseModal = () => {
        setModalVisible(false); // 关闭弹窗
        setEditingConfig(null); // 清空正在编辑的配置
        form.resetFields(); // 重置表单字段和验证状态
    };

    const handleSubmit = async () => {
        try {
            // 先验证表单
            await form.validateFields();
            // 显示确认弹窗
            setConfirmModalVisible(true);
        } catch (error) {
            // 表单验证失败，不显示确认弹窗
            console.error('Form validation failed:', error);
        }
    };

    const handleConfirmSubmit = async () => {
        try {
            const values = await form.validateFields();
            // 关闭确认弹窗
            setConfirmModalVisible(false);
            
            if (modalMode === 'create') {
                // 创建模式
                if (!values.warehouseIds || values.warehouseIds.length === 0) {
                    message.error(t('config.entryConfig.messages.selectAtLeastOneWarehouse'));
                    return;
                }

                const timeRange = values.templateEditTimeRange;
                const input: CreateWarehousePlanningEntryConfigInput = {
                    warehouseIds: values.warehouseIds,
                    entryNode: values.entryNode,
                    isActive: values.isActive,
                    splitOrderConfig: values.splitOrderConfig ?? [],
                    templateEditStartTime: timeRange?.[0]?.format('HH:mm:ss'),
                    templateEditEndTime: timeRange?.[1]?.format('HH:mm:ss')
                };

                const result = await createConfigs({
                    variables: { input }
                });

                // 处理返回结果
                if (result.data?.result) {
                    const createResult = result.data.result;
                    // 如果有跳过或部分成功的情况，显示详细信息
                    if (createResult.skippedCount > 0 || (createResult.successCount > 0 && createResult.skippedCount > 0)) {
                        const formattedMessage = formatBatchCreateResult(createResult);
                        setInfoModalContent(formattedMessage);
                        setInfoModalVisible(true);
                    } else if (createResult.successCount > 0) {
                        // 全部成功
                        message.success(t('config.entryConfig.messages.createSuccess'));
                    }
                } else {
                    message.success(t('config.entryConfig.messages.createSuccess'));
                }

                resetFormAndCloseModal();
                // 手动刷新列表
                await refetch();
            } else {
                // 编辑模式
                if (!editingConfig) return;

                const editTimeRange = values.templateEditTimeRange;
                const input: UpdateWarehousePlanningEntryConfigInput = {
                    entryNode: values.entryNode,
                    isActive: values.isActive,
                    splitOrderConfig: values.splitOrderConfig ?? [],
                    templateEditStartTime: editTimeRange?.[0]?.format('HH:mm:ss'),
                    templateEditEndTime: editTimeRange?.[1]?.format('HH:mm:ss')
                };

                await updateConfig({
                    variables: {
                        id: editingConfig.id,
                        input
                    }
                });

                message.success(t('config.entryConfig.messages.updateSuccess'));
                resetFormAndCloseModal();
                // 手动刷新列表
                await refetch();
            }
        } catch (error: any) {
            // 处理错误
            const errorMessage = error.message || t('config.entryConfig.messages.operationFailed');
            message.error(errorMessage);
        }
    };

    const handleCancel = () => {
        resetFormAndCloseModal();
    };

    // 分页变化处理
    const handleTableChange = (newPagination: any) => {
        setPagination({
            ...pagination,
            current: newPagination.current,
            pageSize: newPagination.pageSize
        });
    };

    const columns: TableColumnsType<WarehousePlanningEntryConfigType> = [
        {
            title: t('config.entryConfig.columns.warehouse'),
            dataIndex: 'warehouseName',
            key: 'warehouseName',
            align: 'left' as const,
            fixed: 'left' as const,
            ellipsis: false,
            render: (name: string) => <span style={{ whiteSpace: 'nowrap' }}>{name || '-'}</span>
        },
        {
            title: t('config.entryConfig.columns.entryNode'),
            dataIndex: 'entryNodeDescription',
            key: 'entryNodeDescription',
            align: 'left' as const,
            fixed: 'left' as const,
            ellipsis: false,
            render: (description: string) => <span style={{ whiteSpace: 'nowrap' }}>{description || '-'}</span>
        },
        {
            title: t('config.entryConfig.columns.isActive'),
            dataIndex: 'isActive',
            key: 'isActive',
            align: 'left' as const,
            ellipsis: false,
            render: (isActive: boolean) => {
                const text = isActive ? t('config.entryConfig.isActive.yes') : t('config.entryConfig.isActive.no');
                return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            }
        },
        {
            title: t('config.entryConfig.columns.splitOrderConfig'),
            dataIndex: 'splitOrderConfig',
            key: 'splitOrderConfig',
            align: 'left' as const,
            ellipsis: false,
            render: (config: number[]) => {
                const labels: Record<number, string> = { 1: t('splitOrder.drawer.mapSplit'), 2: t('splitOrder.drawer.zipcodeSplit'), 3: t('splitOrder.drawer.stopSplit'), 4: t('config.entryConfig.algorithmSplit') };
                // 未配置时按算法分单默认展示（与分单页默认一致）
                const effective = !config || config.length === 0 ? [4] : config;
                return <span style={{ whiteSpace: 'nowrap' }}>{effective.map(n => labels[n] || n).join('、')}</span>;
            }
        },
        {
            title: t('config.entryConfig.columns.templateEditTime'),
            key: 'templateEditTime',
            align: 'left' as const,
            ellipsis: false,
            render: (_: any, record: WarehousePlanningEntryConfigType) => {
                if (record.templateEditStartTime && record.templateEditEndTime) {
                    const start = record.templateEditStartTime.substring(0, 5);
                    const end = record.templateEditEndTime.substring(0, 5);
                    return <span style={{ whiteSpace: 'nowrap' }}>{`${start} - ${end}`}</span>;
                }
                return <span>-</span>;
            }
        },
        {
            title: t('config.entryConfig.columns.createdBy'),
            dataIndex: 'createdBy',
            key: 'createdBy',
            align: 'left' as const,
            ellipsis: false,
            render: (value: string) => <span style={{ whiteSpace: 'nowrap' }}>{value || '-'}</span>
        },
        {
            title: t('config.entryConfig.columns.createdAt'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            align: 'left' as const,
            ellipsis: false,
            render: (value: string) => {
                const text = value ? formatISODateTime(value) : '-';
                return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            }
        },
        {
            title: t('config.entryConfig.columns.updatedBy'),
            dataIndex: 'updatedBy',
            key: 'updatedBy',
            align: 'left' as const,
            ellipsis: false,
            render: (value: string) => <span style={{ whiteSpace: 'nowrap' }}>{value || '-'}</span>
        },
        {
            title: t('config.entryConfig.columns.updatedAt'),
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            align: 'left' as const,
            ellipsis: false,
            render: (value: string) => {
                const text = value ? formatISODateTime(value) : '-';
                return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            }
        },
        {
            title: t('config.entryConfig.columns.action'),
            key: 'action',
            align: 'left' as const,
            fixed: 'right' as const,
            width: 100,
            render: (_, record) => (
                <Button
                    type="link"
                    onClick={() => handleEdit(record)}
                    style={{ paddingLeft: 0 }}
                >
                    <span style={{ whiteSpace: 'nowrap' }}>{t('config.entryConfig.buttons.edit')}</span>
                </Button>
            )
        }
    ];

    return (
        <div>
            <div className="mb-4">
                <WarehousePlanningEntryConfigFilter
                    onFilter={setFilters}
                    warehouseOptions={warehouseOptions}
                    entryNodeOptions={entryNodeOptions}
                    isActiveOptions={isActiveOptions}
                    onCreate={handleCreate}
                />
            </div>
            <Table
                columns={columns}
                dataSource={pagedConfigs}
                loading={loading}
                rowKey="id"
                pagination={false}
                // 列宽按内容自适应、表头不换行，超出时横向滚动；仓库、准入节点固定左侧，操作固定右侧
                scroll={{ x: 'max-content' }}
                className="mb-4 [&_.ant-table-thead_th]:whitespace-nowrap"/>

            {/* 分页 */}
            <TablePaginationBar
                total={configs.length}
                current={pagination.current}
                pageSize={pagination.pageSize}
                onChange={(page, size) => handleTableChange({ current: page, pageSize: size })}
                pageSizeOptions={['10', '20', '50', '100']}
                showQuickJumper={false}
            />
            <Modal
                title={modalMode === 'create' ? t('config.entryConfig.modal.createTitle') : t('config.entryConfig.modal.editTitle')}
                open={modalVisible}
                onOk={handleSubmit}
                onCancel={handleCancel}
                confirmLoading={modalMode === 'create' ? createLoading : updateLoading}
                okText={t('config.entryConfig.buttons.confirm')}
                cancelText={t('config.entryConfig.buttons.cancel')}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        label={t('config.entryConfig.form.warehouse')}
                        name={modalMode === 'create' ? 'warehouseIds' : 'warehouse'}
                        rules={modalMode === 'create' ? [{ required: true, message: String(t('config.entryConfig.validation.selectAtLeastOneWarehouse')) }] : []}
                    >
                        {modalMode === 'create' ? (
                            <Select
                                mode="multiple"
                                options={availableWarehouseOptions}
                                placeholder={String(t('config.entryConfig.placeholders.selectWarehouses'))}
                                showSearch //启用搜索输入框
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                } //自定义过滤函数
                            />
                        ) : (
                            <Select
                                disabled
                                options={warehouseOptions}
                            />
                        )}
                    </Form.Item>
                    <Form.Item
                        label={t('config.entryConfig.form.entryNode')}
                        name="entryNode"
                        rules={[{ required: true, message: String(t('config.entryConfig.validation.selectEntryNode')) }]}
                    >
                        <Select options={entryNodeOptions} placeholder={String(t('config.entryConfig.placeholders.selectEntryNode'))} />
                    </Form.Item>
                    <Form.Item
                        label={t('config.entryConfig.form.templateEditTime')}
                        name="templateEditTimeRange"
                    >
                        <TimePicker.RangePicker format="HH:mm:ss" style={{width: '100%'}} />
                    </Form.Item>
                    <Form.Item
                        label={t('config.entryConfig.form.splitOrderConfig')}
                        name="splitOrderConfig"
                    >
                        <Select
                            mode="multiple"
                            placeholder={String(t('config.entryConfig.placeholders.selectSplitOrderConfig'))}
                            options={[
                                { label: t('splitOrder.drawer.mapSplit'), value: 1 },
                                { label: t('splitOrder.drawer.zipcodeSplit'), value: 2 },
                                { label: t('splitOrder.drawer.stopSplit'), value: 3 },
                                { label: t('config.entryConfig.algorithmSplit'), value: 4 },
                            ]}
                        />
                    </Form.Item>
                    <Form.Item
                        label={t('config.entryConfig.form.isActive')}
                        name="isActive"
                        rules={[{ required: true, message: String(t('config.entryConfig.validation.selectIsActive')) }]}
                    >
                        <Select options={isActiveOptions} placeholder={String(t('config.entryConfig.placeholders.selectIsActive'))} />
                    </Form.Item>
                </Form>
            </Modal>
            {/* 确认保存配置弹窗 */}
            <Modal
                title={t('config.entryConfig.modal.confirmTitle')}
                open={confirmModalVisible}
                onOk={handleConfirmSubmit}
                onCancel={() => setConfirmModalVisible(false)}
                confirmLoading={modalMode === 'create' ? createLoading : updateLoading}
                okText={t('config.entryConfig.buttons.confirm')}
                cancelText={t('config.entryConfig.buttons.cancel')}
            >
                <div>{t('config.entryConfig.modal.confirmContent')}</div>
            </Modal>
            {/* 提示信息弹窗 */}
            <Modal
                title={t('config.entryConfig.modal.infoTitle')}
                open={infoModalVisible}
                onOk={() => setInfoModalVisible(false)}
                onCancel={() => setInfoModalVisible(false)}
                okText={t('config.entryConfig.buttons.confirm')}
                footer={[
                    <Button key="ok" type="primary" onClick={() => setInfoModalVisible(false)}>
                        {t('config.entryConfig.buttons.confirm')}
                    </Button>
                ]}
                closable={true}
            >
                <div style={{ whiteSpace: 'pre-wrap' }}>{infoModalContent}</div>
            </Modal>
        </div>
    );
};

export default WarehousePlanningEntryConfigPage;

